import {
    ApolloError,
    DocumentNode,
    FetchResult,
    useMutation,
} from '@apollo/client'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { useCallback } from 'react'

import {
    AcceptAllTrackedChange,
    AcceptTrackedChange,
    RejectAllTrackedChange,
    RejectTrackedChange,
} from 'api/graphQL/mutations/change.mutations'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useParams } from 'react-router-dom'
import { Action } from '../../types/types'

const handleFetchError = (
    error: ApolloError,
    defaultError: string = 'WorkspaceEditor-AcceptTrackedChange'
) => {
    const summarizedError = summarizeApolloError(error, defaultError)
    console.error(summarizedError)
}

const useTrackChangeMutation = (mutation: DocumentNode, label: string) =>
    useMutation(mutation, {
        fetchPolicy: 'no-cache',
        onError: (e: ApolloError) =>
            handleFetchError(e, `WorkspaceEditor-${label}`),
    })

/**
 * A custom hook that provides functionality to fetch and manage TrackChanges data.
 * @template T - The type of the props that extend `TrackChangesApiProps`.
 * @param {string} props.trackChangeId - The ID of the project to fetch data for.
 * @param {string} props.action - The ID of the element to fetch data for.
 * @returns {Object} An object containing the `acceptRejectTrackChangesData` function.
 * @returns {Function} returns.acceptRejectTrackChangesData - A function to fetch the project data.
 */
export const acceptRejectTrackChanges = (): {
    acceptRejectTrackChangesData: (
        trackChangeId: string,
        action: Action
    ) => Promise<
        FetchResult<any, Record<string, any>, Record<string, any>> | undefined
    >
} => {
    const { projectId } = useParams<{ projectId: string }>()
    const { elementId, isProjectProductFamily } = useElementItemShallowSelector(
        (s) => s.elementItemValue
    )
    const { contentItemViewNodes } = useShallowSelector((s) => ({
        contentItemViewNodes: s.contentItemViewNodes,
    }))

    const [acceptChange] = useTrackChangeMutation(
        AcceptTrackedChange,
        'AcceptTrackedChange'
    )
    const [rejectChange] = useTrackChangeMutation(
        RejectTrackedChange,
        'RejectTrackedChange'
    )
    const [acceptAllChange] = useTrackChangeMutation(
        AcceptAllTrackedChange,
        'AcceptAllTrackedChange'
    )
    const [rejectAllChange] = useTrackChangeMutation(
        RejectAllTrackedChange,
        'RejectAllTrackedChange'
    )

    const acceptRejectTrackChangesData = useCallback(
        async (
            trackChangeId: string,
            action: Action
        ): Promise<
            | FetchResult<any, Record<string, any>, Record<string, any>>
            | undefined
        > => {
            let variables = {}
            if (action === 'accept' || action === 'discard') {
                variables = {
                    input: { trackChangeId },
                    includeProductTypeInFamilyTrackChanges: isProjectProductFamily,
                }
            } else if (action === 'acceptAll' || action === 'discardAll') {
                const familyId = contentItemViewNodes.find((node) => !!node)
                    ?.productFamilyId
                variables = {
                    input: {
                        elementId,
                        projectId,
                        familyId,
                    },
                    includeProductTypeInFamilyTrackChanges: isProjectProductFamily,
                }
            } else {
                throw new Error(`Unsupported action: ${action}`)
            }
            switch (action) {
                case 'accept':
                    return acceptChange({ variables })
                case 'discard':
                    return rejectChange({ variables })
                case 'acceptAll':
                    return acceptAllChange({ variables })
                case 'discardAll':
                    return rejectAllChange({ variables })
            }
        },
        [contentItemViewNodes]
    )

    return { acceptRejectTrackChangesData }
}
